'use client';

import { type RefObject, useEffect, useRef } from 'react';

interface UseHorizontalAutoScrollOptions {
  /** Scroll speed in px/sec. Default: 30 */
  speedPxPerSec?: number;
  /** When true, the auto-scroll is paused (e.g. on hover or while a
   *  modal/lightbox is open). */
  paused?: boolean;
}

/**
 * Slowly scrolls the referenced element horizontally on a
 * `requestAnimationFrame` loop. When `scrollLeft` reaches the end, it wraps
 * back to the start so scrolling repeats indefinitely. Skips work when the
 * element's content fits in view.
 *
 * The `paused` flag is read from a ref each frame, so toggling it mid-loop
 * doesn't restart the rAF cycle.
 */
export function useHorizontalAutoScroll(
  scrollRef: RefObject<HTMLElement | null>,
  options?: UseHorizontalAutoScrollOptions,
): void {
  const { speedPxPerSec = 30, paused = false } = options ?? {};

  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    if (element.scrollWidth <= element.clientWidth) return;

    let rafId = 0;
    let lastTimestamp = 0;

    function tick(timestamp: number) {
      if (lastTimestamp === 0) lastTimestamp = timestamp;
      const elapsedSec = (timestamp - lastTimestamp) / 1000;
      lastTimestamp = timestamp;

      if (!pausedRef.current && element) {
        const maxScroll = element.scrollWidth - element.clientWidth;
        const next = element.scrollLeft + speedPxPerSec * elapsedSec;
        element.scrollLeft = next >= maxScroll ? 0 : next;
      }

      rafId = requestAnimationFrame(tick);
    }

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [scrollRef, speedPxPerSec]);
}
